<template>
  <div class="hot-searches-page">
    <Breadcrumbs :items="breadcrumbItems" />
    <div class="hot-searches-card">
      <div class="card-header">
        <h6>{{ $t('lbl_HotSearches_Title') }}</h6>
        <p class="text-muted">{{ $t('lbl_HotSearches_Description') }}</p>
      </div>
      <div class="card-body">
        <!-- Frontend preview pills — exactly what users see in the search bar -->
        <div class="suggest-pills" v-if="suggestItems.length">
          <div class="suggest-header">
            <span class="suggest-label">
              <i class="fa fa-globe"></i>
              {{ $t('lbl_HotSearches_SuggestHint', { count: suggestItems.length }) }}
            </span>
            <span class="suggest-badge">
              <i class="fa fa-eye"></i>
              {{ $t('lbl_HotSearches_FrontendPreview') }}
            </span>
          </div>
          <div class="suggest-pills-inner">
            <button
              v-for="(item, idx) in suggestItems"
              :key="idx"
              class="suggest-pill"
              @click="onSuggestClick(item)"
            >
              {{ item.keyword }}
            </button>
          </div>
        </div>

        <b-overlay :show="loading" rounded="sm">
          <b-tabs v-model="activeTabIndex" @input="onTabChange" class="hot-searches-tabs">
            <b-tab
              :title="tabLabels[0]"
              :title-link-class="activeTabIndex === 0 ? 'active-lang' : ''"
            >
              <Grid ref="grid0" v-if="loadedTabs.has(0)" :lang-id="1252" :show-only-active.sync="showOnlyActive" @dataChanged="onDataChanged" />
            </b-tab>
            <b-tab
              :title="tabLabels[1]"
              :title-link-class="activeTabIndex === 1 ? 'active-lang' : ''"
            >
              <Grid ref="grid1" v-if="loadedTabs.has(1)" :lang-id="950" :show-only-active.sync="showOnlyActive" @dataChanged="onDataChanged" />
            </b-tab>
            <b-tab
              :title="tabLabels[2]"
              :title-link-class="activeTabIndex === 2 ? 'active-lang' : ''"
            >
              <Grid ref="grid2" v-if="loadedTabs.has(2)" :lang-id="936" :show-only-active.sync="showOnlyActive" @dataChanged="onDataChanged" />
            </b-tab>
          </b-tabs>
        </b-overlay>
      </div>
    </div>
  </div>
</template>

<script>
import Grid from './components/Grid';
import { hotSearchService } from '@/_services/hotSearch.service';

export default {
  name: 'HotSearchesDefault',
  components: {
    Grid,
  },
  data() {
    return {
      loading: false,
      activeTabIndex: 0,
      loadedTabs: new Set([0]),
      langIds: [1252, 950, 936],
      suggestItems: [],
      showOnlyActive: false,
      tabLabels: [
        this.$t('lbl_HotSearches_English'),
        this.$t('lbl_HotSearches_TradChinese'),
        this.$t('lbl_HotSearches_SimpChinese'),
      ],
    };
  },
  computed: {
    selectedLangId() {
      return this.langIds[this.activeTabIndex];
    },
    breadcrumbItems() {
      return [
        { text: this.$t('lbl_HotSearches_Title'), to: '/hotSearches' },
      ];
    },
  },
  async created() {
    const currentLangId = localStorage.getItem('currentLangId');
    if (currentLangId) {
      const idx = this.langIds.indexOf(Number(currentLangId));
      if (idx !== -1) {
        this.activeTabIndex = idx;
        this.loadedTabs = new Set([0, idx]);
      }
    }
    await this.loadSuggestItems();
  },
  methods: {
    async loadSuggestItems(langId) {
      try {
        langId = langId || Number(localStorage.getItem('currentLangId')) || 1252;
        const res = await hotSearchService.GetSearchBarHotSearches(langId);
        
        if (res.isSuccessful || res.IsSuccessful) {
          const now = new Date();
          this.suggestItems = (res.data || [])
            .filter((item) => {
              const from = item.effectiveDateFrom || item.EffectiveDateFrom;
              const to = item.effectiveDateTo || item.EffectiveDateTo;
              if (from && new Date(from) > now) return false;
              if (to && new Date(to) < now) return false;
              return true;
            })
            .map((item) => ({
              keyword: item.keyword || item.Keyword || item.text || item.Text || '',
              sortOrder: item.sortOrder || item.SortOrder || item.order || item.Order || 0,
              type: item.type || item.Type || 'keyword',
              tagId: item.tagId || item.TagId || null,
            }));
        }
      } catch (error) {
        // Failed to load suggest items — pills section stays hidden
      }
    },
    onTabChange(tabIndex) {
      this.activeTabIndex = tabIndex;
      this.loadedTabs.add(tabIndex);
      // Reload suggest pills for the selected language
      this.loadSuggestItems(this.langIds[tabIndex]);
      // Refresh grid data for the active tab — use $nextTick to ensure Vue
      // has finished rendering the tab content before calling loadData
      this.$nextTick(() => {
        const gridRef = this.$refs[`grid${tabIndex}`];
        if (gridRef && gridRef.loadData) {
          gridRef.loadData(true); // force re-fetch
        }
      });
    },
    onSuggestClick(item) {
      navigator.clipboard?.writeText(item.keyword).then(() => {
        this.$swal({
          title: this.$t('lbl_HotSearches_SuggestCopied'),
          icon: 'success',
          timer: 1500,
          showConfirmButton: false,
        });
      });
    },
    onDataChanged(langId) {
      this.loadSuggestItems(langId);
    },
  },
};
</script>

<style scoped>
.hot-searches-page {
  padding-bottom: 1.5rem;
}

.hot-searches-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.hot-searches-card .card-header {
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid #f0f0f0;
  background: linear-gradient(135deg, #fffaf5 0%, #fff 100%);
}

.hot-searches-card .card-header h6 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
}

.hot-searches-card .card-header .text-muted {
  margin: 0.25rem 0 0;
  font-size: 0.8125rem;
  color: #6b7280;
  line-height: 1.5;
}

.hot-searches-card .card-body {
  padding: 1rem 1.5rem 0;
}

/* Suggestion pills */
.suggest-pills {
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f0f0f0;
}

.suggest-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.625rem;
  flex-wrap: wrap;
}

.suggest-label {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0;
}

.suggest-label i {
  margin-right: 0;
  color: #059669;
}

.suggest-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.5rem;
  background: #fef3c7;
  color: #92400e;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 600;
}

.suggest-badge i {
  font-size: 0.75rem;
}

.suggest-pills-inner {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.suggest-pill {
  border: 0;
  color: rgba(0, 0, 0, 0.80);
  background: #F9F9F9;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  white-space: nowrap;
}

.suggest-pill:hover {
  background: #e8e8e8;
}

/* Tab styling */
.hot-searches-tabs .nav-tabs {
  border-bottom: 2px solid #e5e7eb;
}

.hot-searches-tabs .nav-tabs .nav-link {
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  padding: 0.625rem 1.25rem;
  color: #6b7280;
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  border-radius: 0;
}

.hot-searches-tabs .nav-tabs .nav-link:hover {
  color: #ea580c;
  border-bottom-color: #fed7aa;
}

.hot-searches-tabs .nav-tabs .nav-link.active-lang {
  color: #ea580c;
  border-bottom-color: #ea580c;
  font-weight: 600;
  background: transparent;
}

.hot-searches-tabs .tab-content {
  padding: 1rem 0;
}
</style>
